import clsx from 'clsx'
import moment from 'moment'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {KTSVG, useDisabled} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {PageTitle} from '../../../../_gori/layout/core'
import {BranchesMenuModal} from '../modals/BranchesMenuModal'
import {OrganizationAccounts} from './OrganizationAccounts'
import {OrganizationOverview} from './OrganizationOverview'
import {useOrganizationBranches} from '../providers/OrganizationBranchesProvider'
import {getRangeForPreset, TimeRange, TIME_RANGES} from '../core/_const'
import OrganizationService from '../core/_requests'
import {
  applyOrganizationAccountsFiltersToSearchParams,
  buildOrganizationAccountsParams,
  parseOrganizationAccountsFiltersFromSearchParams,
} from '../helpers/organizationAccountsFilterHelpers'
import {OrganizationAccountsFilterValues} from '../modals/OrganizationAccountsFilter'
import type {OrganizationAccount, OrganizationAccountsPagination} from '../core/_types'
import {useAuth} from '../../auth'
import {DEFAULT_PAGE} from '../../../../_gori/constants'

type OrganizationsTab = 'overview' | 'accounts'

const getTabFromParams = (tab: string | null): OrganizationsTab =>
  tab === 'accounts' ? 'accounts' : 'overview'

const OrganizationsPage: FC = () => {
  const intl = useIntl()
  const {loadingSwitch} = useAuth()
  const {setPageDisabled} = useDisabled()
  const {cancelAndCreateNewToken, isCancel} = useCancelToken()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = getTabFromParams(searchParams.get('tab'))
  const [timeRange, setTimeRange] = useState<TimeRange>('ytd')
  const [isCustomRange, setIsCustomRange] = useState(false)
  const [dateRange, setDateRange] = useState(() => getRangeForPreset('ytd'))
  const [pickerKey, setPickerKey] = useState(0)
  const [showBranchesMenuModal, setShowBranchesMenuModal] = useState(false)
  const [accountsTabCount, setAccountsTabCount] = useState(0)
  const [tableData, setTableData] = useState<OrganizationAccount[]>([])
  const [pagination, setPagination] = useState<Partial<OrganizationAccountsPagination>>({})
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const {menuData, appliedAccountIds, isBranchesReady, applyMenuDataFromModal} =
    useOrganizationBranches()

  const accountFilters = useMemo(
    () => parseOrganizationAccountsFiltersFromSearchParams(searchParams),
    [searchParams]
  )
  const searchParamsKey = searchParams.toString()

  const handleAccountFiltersChange = useCallback(
    (filters: OrganizationAccountsFilterValues | null) => {
      const next = applyOrganizationAccountsFiltersToSearchParams(searchParams, filters)
      next.set('page', DEFAULT_PAGE)
      setSearchParams(next, {replace: true})
    },
    [searchParams, setSearchParams]
  )

  const loadAccounts = useCallback(async () => {
    const shouldDisablePage = activeTab === 'accounts'

    if (shouldDisablePage) {
      setPageDisabled(true)
    }

    try {
      const response = await OrganizationService.getAccounts(
        buildOrganizationAccountsParams(searchParams, appliedAccountIds),
        {cancelToken: cancelAndCreateNewToken()}
      )
      const accounts = (response.accounts ?? []).map((account) => ({
        ...account,
        canCheck: true,
      }))

      setTableData(accounts)
      setPagination(response.pagination ?? {})
      setAccountsTabCount(response.statistics?.count ?? 0)
    } catch (error: unknown) {
      if (isCancel(error)) return
      setTableData([])
      setPagination({})
      setAccountsTabCount(0)
    } finally {
      if (shouldDisablePage) {
        setPageDisabled(false)
      }
      setLoadingAccounts(false)
    }
  }, [
    activeTab,
    appliedAccountIds,
    cancelAndCreateNewToken,
    isCancel,
    searchParams,
    setPageDisabled,
  ])

  useEffect(() => {
    if (loadingSwitch || !isBranchesReady) {
      return
    }

    loadAccounts()
  }, [isBranchesReady, loadingSwitch, loadAccounts, searchParamsKey])
  const analyticsFilters = useMemo(
    () => ({
      accountIds: appliedAccountIds,
      timeRange,
      isCustomRange,
      dateRange,
    }),
    [appliedAccountIds, dateRange, isCustomRange, timeRange]
  )

  const setActiveTab = (tab: OrganizationsTab) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'overview') {
      next.delete('tab')
    } else {
      next.set('tab', tab)
    }
    setSearchParams(next, {replace: true})
  }

  const handleSelectTimeRange = (range: TimeRange) => {
    setIsCustomRange(false)
    setTimeRange(range)
    setDateRange(getRangeForPreset(range))
  }

  // Force remount DateRangePicker when locale changes
  useEffect(() => {
    setPickerKey((prev) => prev + 1)
  }, [intl])

  const dateRangePickerLocale = useMemo(
    () => ({
      customRangeLabel: intl.formatMessage({id: 'CUSTOM_RANGE'}),
      cancelLabel: intl.formatMessage({id: 'CANCEL'}),
      applyLabel: intl.formatMessage({id: 'APPLY'}),
      fromLabel: intl.formatMessage({id: 'FROM'}),
      toLabel: intl.formatMessage({id: 'TO'}),
      daysOfWeek: [
        intl.formatMessage({id: 'SU'}),
        intl.formatMessage({id: 'MO'}),
        intl.formatMessage({id: 'TU'}),
        intl.formatMessage({id: 'WE'}),
        intl.formatMessage({id: 'TH'}),
        intl.formatMessage({id: 'FR'}),
        intl.formatMessage({id: 'SA'}),
      ],
      monthNames: [
        intl.formatMessage({id: 'JANUARY'}),
        intl.formatMessage({id: 'FEBRUARY'}),
        intl.formatMessage({id: 'MARCH'}),
        intl.formatMessage({id: 'APRIL'}),
        intl.formatMessage({id: 'MAY'}),
        intl.formatMessage({id: 'JUNE'}),
        intl.formatMessage({id: 'JULY'}),
        intl.formatMessage({id: 'AUGUST'}),
        intl.formatMessage({id: 'SEPTEMBER'}),
        intl.formatMessage({id: 'OCTOBER'}),
        intl.formatMessage({id: 'NOVEMBER'}),
        intl.formatMessage({id: 'DECEMBER'}),
      ],
    }),
    [intl]
  )

  const handleApplyCustomDateRange = (
    _event: unknown,
    picker: {startDate: moment.Moment; endDate: moment.Moment}
  ) => {
    setIsCustomRange(true)
    setDateRange({start: moment(picker.startDate), end: moment(picker.endDate)})
  }

  return (
    <CSSTransition in timeout={300} classNames='fade' unmountOnExit>
      <div className='d-flex flex-column h-100 organizations-page'>
        <PageTitle
          trailingActions={
            <button
              type='button'
              className='organizations-page__account-filter-btn'
              onClick={() => setShowBranchesMenuModal(true)}
            >
              {intl.formatMessage({id: 'ADJUST_ACCOUNT_FILTER'})}
            </button>
          }
        >
          {intl.formatMessage({id: 'ORGANIZATIONS'})}
        </PageTitle>

        <BranchesMenuModal
          show={showBranchesMenuModal}
          handleClose={() => setShowBranchesMenuModal(false)}
          initialMenuData={menuData}
          onApply={(nextMenuData) => applyMenuDataFromModal(nextMenuData)}
        />

        <div className='organizations-page__account-filter-btn-container pb-5 justify-content-end'>
          <button
            type='button'
            className='organizations-page__account-filter-btn'
            onClick={() => setShowBranchesMenuModal(true)}
          >
            {intl.formatMessage({id: 'ADJUST_ACCOUNT_FILTER'})}
          </button>
        </div>
        <div className='d-flex justify-content-between align-items-start flex-column-reverse flex-md-row'>
          <div className='table-tabs'>
            <div
              className={clsx('table-tabs__tab', {active: activeTab === 'overview'})}
              onClick={() => setActiveTab('overview')}
            >
              <div>{intl.formatMessage({id: 'OVERVIEW'})}</div>
            </div>
            <div
              className={clsx('table-tabs__tab', {active: activeTab === 'accounts'})}
              onClick={() => setActiveTab('accounts')}
            >
              <div>{intl.formatMessage({id: 'ACCOUNTS'})}</div>
              {accountsTabCount > 0 && (
                <div className={clsx('table-tabs__tab__count', {active: activeTab === 'accounts'})}>
                  {accountsTabCount}
                </div>
              )}
            </div>
          </div>
          {activeTab === 'overview' && (
            <div className='organizations-time-range'>
              <div
                className='organizations-time-range__segment'
                role='group'
                aria-label='Time range'
              >
                {TIME_RANGES.map((range) => (
                  <span
                    key={range.id}
                    className={clsx('organizations-time-range__item align-items-center', {
                      'organizations-time-range__item--active':
                        !isCustomRange && timeRange === range.id,
                    })}
                    onClick={() => handleSelectTimeRange(range.id)}
                  >
                    {range.label}
                  </span>
                ))}
              </div>
              <DateRangePicker
                key={pickerKey}
                initialSettings={{
                  locale: dateRangePickerLocale,
                  startDate: dateRange.start,
                  endDate: dateRange.end,
                  opens: 'left',
                  ranges: false,
                  alwaysShowCalendars: true,
                  maxDate: moment(),
                }}
                onApply={handleApplyCustomDateRange}
              >
                <button
                  type='button'
                  className={clsx('organizations-time-range__calendar-btn', {
                    'organizations-time-range__calendar-btn--active': isCustomRange,
                  })}
                  aria-label={intl.formatMessage({id: 'CUSTOM_RANGE'})}
                >
                  <KTSVG path='media/gori/chatbot/calendar.svg' svgClassName='' small={false} />
                </button>
              </DateRangePicker>
            </div>
          )}
        </div>

        <div className='rounded-start-0 d-flex flex-column flex-column-fluid'>
          {activeTab === 'overview' ? (
            <OrganizationOverview {...analyticsFilters} />
          ) : (
            <OrganizationAccounts
              tableData={tableData}
              pagination={pagination}
              loadingFirst={loadingAccounts}
              accountFilters={accountFilters}
              onAccountFiltersChange={handleAccountFiltersChange}
              onReloadAccounts={loadAccounts}
            />
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export {OrganizationsPage}
